import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import Grid from "@mui/material/Grid"
import Icon from "@mui/material/Icon"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import DashboardLayout from "examples/LayoutContainers/DashboardLayout"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"
import KpiTile from "./components/KpiTile"
import ManagerWorkspaceHeader from "./components/ManagerWorkspaceHeader"
import StatusBadge from "./components/StatusBadge"
import ProcurementFilterBar from "./components/ProcurementFilterBar"
import ScopedDataTable from "./components/ScopedDataTable"
import useProcurementFilters from "./hooks/useProcurementFilters"
import useProcurementListFilters from "./hooks/useProcurementListFilters"
import { useGetOrdersForApprovalQuery } from "shared/redux/endpoints/procurementApprovals"
import { useGetPurchaseSuppliersQuery } from "shared/redux/endpoints/procurementPurchases"
import { formatDate, formatVND, normalizeOrderOrGroupItem } from "./constants"

const FILTER_DEFAULTS = {
  search: "",
  category: "",
  region: "",
  center: "",
  item: "",
  supplier: "",
  fromDate: "",
  toDate: "",
}

// ROLE_PROCUREMENT_MANAGER approvals queue.
// GET /private/procurement/order-requests/for-approval — server-side filter
// is `category` only; search is applied client-side over the fetched
// page. No KPI/summary endpoint exists yet (GET .../approvals/summary and
// GET /me/permissions both 404 on the backend today), so this page doesn't
// call either — re-add the KPI header and the permission gate once they ship.
//
// `groupOnly`: same query, filtered client-side to type==="group" rows only —
// the dedicated "Approve Group Order" screen. No separate group-order
// approval endpoint exists yet, so this reuses the mixed order/group feed.
const ApprovalInboxContainer = ({ groupOnly = false }) => {
  const navigate = useNavigate()
  const { filters, setFilters, resetFilters } = useProcurementFilters(FILTER_DEFAULTS)
  // Group rows have no detail page of their own (they're just a bundle of
  // orders) — "Review" opens this dialog instead of navigating, same pattern
  // as OrdersContainer's group dialog.
  const [groupDialog, setGroupDialog] = useState(null)

  const { categoryOptions, centerOptions, itemOptions, itemsLoading, queryFilters } =
    useProcurementListFilters(filters)
  const { data: suppliers } = useGetPurchaseSuppliersQuery()
  const supplierOptions = useMemo(
    () => (suppliers || []).map((s) => ({ value: String(s.id), label: s.name })),
    [suppliers]
  )

  // `supplier` matches an order/group with at least one vendor proposal
  // (selected or not) from that supplier — same shared date/category/region/
  // center/item/supplier set as OrdersWorkspace.
  const { data, isFetching, isError, refetch } = useGetOrdersForApprovalQuery({
    ...queryFilters,
    category: filters.category || undefined,
    supplier: filters.supplier || undefined,
    size: 100,
  })

  // GET .../for-approval returns a MIXED list (item.type "order"|"group"),
  // same shape as GET /order-requests — see normalizeOrderOrGroupItem. A
  // group's category/status/total are already derived server-side from its
  // child orders.
  const rows = useMemo(() => {
    const items = (data?.items || []).map((o) => {
      const norm = normalizeOrderOrGroupItem(o)
      return {
        id: norm.id,
        type: norm.type,
        orCode: norm.code,
        title: norm.title,
        requesterName: norm.requesterName,
        region: norm.region,
        categoryName: norm.categoryName,
        total: norm.total,
        urgency: norm.urgency,
        submittedAt: o.submittedAt,
        currentStepNo: o.currentStepNo,
        purchases: norm.purchases,
        orders: norm.orders,
        overBudget: norm.overBudget,
      }
    })
    const q = filters.search.trim().toLowerCase()
    return items.filter((r) => {
      if (groupOnly && r.type !== "group") return false
      if (q && !`${r.orCode} ${r.title} ${r.requesterName}`.toLowerCase().includes(q)) return false
      return true
    })
  }, [data, filters.search, groupOnly])

  const openDetail = (id) => navigate(`/procurement/orders/${id}`)
  const openRow = (row) => (row.type === "group" ? setGroupDialog(row) : openDetail(row.id))
  const hasActiveFilters = Boolean(
    filters.search ||
      filters.category ||
      filters.region ||
      filters.center ||
      filters.item ||
      filters.supplier ||
      filters.fromDate ||
      filters.toDate
  )

  const columns = useMemo(
    () => [
      {
        Header: "Type",
        accessor: "type",
        width: 70,
        Cell: ({ value }) => (
          <MDBox display="flex" alignItems="center" gap={0.5}>
            {value === "group" && (
              <Icon fontSize="small" sx={{ color: "text.secondary" }}>
                layers
              </Icon>
            )}
            <MDTypography variant="caption" color="text">
              {value === "group" ? "Group" : "Order"}
            </MDTypography>
          </MDBox>
        ),
      },
      {
        Header: "OR Code",
        accessor: "orCode",
        width: 90,
        Cell: ({ value, row }) => (
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <MDTypography variant="button" fontWeight="medium">
              {value}
            </MDTypography>
            {row.original.purchases?.length > 0 && (
              <MDTypography variant="caption" color="text">
                · {row.original.purchases.length} PO
              </MDTypography>
            )}
          </MDBox>
        ),
      },
      {
        Header: "Requester",
        accessor: "requesterName",
        width: 130,
        Cell: ({ value }) => value || "—",
      },
      {
        Header: "Category",
        accessor: "categoryName",
        Cell: ({ value }) => value || "—",
      },
      { Header: "Region", accessor: "region", width: 70 },
      {
        Header: "Total",
        accessor: "total",
        align: "right",
        width: 140,
        Cell: ({ value }) => formatVND(value),
      },
      {
        Header: "Urgency",
        accessor: "urgency",
        width: 70,
        Cell: ({ value }) =>
          (value || "").toLowerCase() === "urgent" ? (
            <MDTypography variant="caption" color="error" fontWeight="bold" textTransform="uppercase">
              Urgent
            </MDTypography>
          ) : (
            <MDTypography variant="caption" color="text" textTransform="capitalize">
              {(value || "normal").toLowerCase()}
            </MDTypography>
          ),
      },
      {
        Header: "Submitted",
        accessor: "submittedAt",
        Cell: ({ value }) => formatDate(value),
      },
      {
        Header: "Step",
        accessor: "currentStepNo",
        width: 70,
        Cell: ({ value }) => `Step ${value ?? "—"}`,
      },
      {
        Header: "",
        accessor: "id",
        align: "right",
        width: 90,
        Cell: ({ row }) => (
          <MDButton size="small" variant="text" color="info" onClick={() => openRow(row.original)}>
            Review
          </MDButton>
        ),
      },
    ],
    [openRow]
  )

  return (
    <DashboardLayout>
      <MDBox p={3}>
        <ManagerWorkspaceHeader
          title={groupOnly ? "Approve Group Order" : "Approve Order"}
          breadcrumb="Procurement • Manager workspace"
          description={
            groupOnly
              ? "Order groups currently waiting for your decision."
              : "Review submitted orders that are currently waiting for your decision. Pending items with routing problems stay out of this queue until an admin fixes them."
          }
          activeTab={groupOnly ? "approve-group-orders" : "approvals"}
        />

        <Grid container spacing={2} mb={1}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiTile
              label="Awaiting My Approval"
              value={isFetching ? "—" : rows.length}
              icon="fact_check"
              color="info"
            />
          </Grid>
        </Grid>

        <ProcurementFilterBar
          fields={["search", "category", "region", "center", "item", "supplier", "createdPeriod"]}
          values={filters}
          onChange={setFilters}
          onReset={resetFilters}
          categoryOptions={categoryOptions}
          centerOptions={centerOptions}
          itemOptions={itemOptions}
          itemsLoading={itemsLoading}
          supplierOptions={supplierOptions}
        />

        <MDBox
          bgColor="white"
          borderRadius="lg"
          shadow="sm"
          p={isError || (!isFetching && rows.length === 0) ? 3 : 1}
          sx={{ border: "1px solid", borderColor: "grey.200" }}
        >
          {isError ? (
            <MDBox textAlign="center" py={2}>
              <MDTypography variant="body2" color="error" mb={1.5}>
                We couldn&apos;t load your approval queue. Please try again.
              </MDTypography>
              <MDButton variant="outlined" color="info" size="small" onClick={refetch}>
                Retry
              </MDButton>
            </MDBox>
          ) : !isFetching && rows.length === 0 ? (
            <MDBox textAlign="center" py={{ xs: 3, md: 4 }}>
              <MDBox
                width="3.5rem"
                height="3.5rem"
                borderRadius="50%"
                bgColor="grey-100"
                color="info"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                mb={2}
              >
                <Icon>{hasActiveFilters ? "search_off" : "verified"}</Icon>
              </MDBox>
              <MDTypography variant="h6" fontWeight="medium" mb={1}>
                {hasActiveFilters ? "No requests match these filters" : "Nothing waiting on you"}
              </MDTypography>
              <MDTypography
                variant="body2"
                color="text"
                mb={hasActiveFilters ? 2 : 0}
                sx={{ maxWidth: 360, mx: "auto" }}
              >
                {hasActiveFilters
                  ? "Try clearing the search or filters to see everything waiting on you."
                  : "When a request reaches a step you approve, it'll show up here."}
              </MDTypography>
              {hasActiveFilters && (
                <MDButton variant="outlined" color="secondary" size="small" onClick={resetFilters}>
                  Clear filters
                </MDButton>
              )}
            </MDBox>
          ) : (
            <>
              <MDBox
                px={2}
                py={1.5}
                borderBottom="1px solid"
                borderColor="grey.200"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
              >
                <MDTypography variant="button" fontWeight="medium">
                  Requests ready for review
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {rows.length} visible request{rows.length === 1 ? "" : "s"}
                </MDTypography>
              </MDBox>
              <ScopedDataTable
                columns={columns}
                rows={rows}
                isLoading={isFetching}
                emptyText="No requests match these filters."
                getRowProps={(row) =>
                  row.original.overBudget
                    ? { sx: { bgcolor: "#fff8e1" } }
                    : row.original.purchases?.length > 0
                    ? { sx: { bgcolor: "grey.100" } }
                    : undefined
                }
                entriesPerPage={{ isShow: false, defaultValue: 20 }}
                pagination={{ isShow: true }}
              />
            </>
          )}
        </MDBox>
      </MDBox>

      <Dialog open={!!groupDialog} onClose={() => setGroupDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {groupDialog?.orCode} · {groupDialog?.title}
        </DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={1}>
            {(groupDialog?.orders || []).map((o) => (
              <MDBox
                key={o.id}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ cursor: "pointer", "&:hover": { bgcolor: "grey.50" } }}
                borderRadius="md"
                px={1}
                py={0.5}
                onClick={() => {
                  setGroupDialog(null)
                  openDetail(o.id)
                }}
              >
                <MDBox>
                  <MDTypography variant="button" fontWeight="medium" color="info">
                    {o.code}
                  </MDTypography>
                  <MDTypography variant="caption" color="text" display="block">
                    {o.title}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <StatusBadge status={o.status} />
                  <MDTypography variant="button" fontWeight="bold">
                    {formatVND(o.totalAmount)}
                  </MDTypography>
                </MDBox>
              </MDBox>
            ))}
            {!groupDialog?.orders?.length && (
              <MDTypography variant="body2" color="text">
                No orders in this group.
              </MDTypography>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={() => setGroupDialog(null)}>
            Close
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  )
}

export default ApprovalInboxContainer
